import { MatchState, MatchFormat, Inning, Delivery, Player } from "../matchTypes";
import { formatPresets } from "../formatPresets";
import { clampOversToFormat } from "../utils/oversConstraints";
import { clampSquadSizeToFormat } from "../utils/squadConstraints";

export function createNewMatch(): MatchState {
  return {
    teamAName: "Team A",
    teamBName: "Team B",
    format: "INDOOR",
    inningsCount: 1,
    currentInnings: 0,
    innings: [
      {
        battingTeam: "A",
        bowlingTeam: "B",
        deliveries: [],
        totalRuns: 0,
        wickets: 0,
        totalBalls: 0,
        legalBalls: 0,
        completed: false,
        oversLimit: 18,
        extras: { wides: 0, noBalls: 0, byes: 0, legByes: 0 },
        dismissalHistory: [],
        reverseBattingActive: false,
      },
    ],
    teamAPlayers: [],
    teamBPlayers: [],
    teamASubstitutes: [],
    teamBSubstitutes: [],
    squadSize: 10,
    teamACaptainId: null,
    teamBCaptainId: null,
    teamAKeeperId: null,
    teamBKeeperId: null,
    strikerId: null,
    nonStrikerId: null,
    bowlerId: null,
    oversLimit: 18,
    ballsPerOver: 6,
    battingOrder: [],
    battingCycleDismissals: 0,
    battingOrderReversed: false,
    toss: {
      winnerTeam: null,
      decision: null,
    },
    officials: {},
    commentary: [],
    matchLocation: "",
    matchDate: new Date().toISOString().split('T')[0],
    matchTime: new Date().toTimeString().slice(0, 5),
    hasActivity: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export function applyFormat(state: MatchState, format: MatchFormat): void {
  const preset = formatPresets[format];
  state.oversLimit = preset.oversLimit;
  state.inningsCount = preset.inningsCount;

  state.oversLimit = clampOversToFormat(state.oversLimit, format);

  if (state.innings.length > 0) {
    state.innings[state.currentInnings].oversLimit = state.oversLimit;
  }

  state.squadSize = clampSquadSizeToFormat(state.squadSize, format);

  if (state.teamAPlayers.length > state.squadSize) {
    state.teamAPlayers = state.teamAPlayers.slice(0, state.squadSize);
    if (!state.teamAPlayers.find(p => p.id === state.teamACaptainId)) {
      state.teamACaptainId = null;
    }
    if (!state.teamAPlayers.find(p => p.id === state.teamAKeeperId)) {
      state.teamAKeeperId = null;
    }
  }

  if (state.teamBPlayers.length > state.squadSize) {
    state.teamBPlayers = state.teamBPlayers.slice(0, state.squadSize);
    if (!state.teamBPlayers.find(p => p.id === state.teamBCaptainId)) {
      state.teamBCaptainId = null;
    }
    if (!state.teamBPlayers.find(p => p.id === state.teamBKeeperId)) {
      state.teamBKeeperId = null;
    }
  }

  if (format === "INDOOR") {
    const inn = state.innings[state.currentInnings];
    const battingTeam = inn.battingTeam === "A" ? state.teamAPlayers : state.teamBPlayers;
    state.battingOrder = battingTeam.map(p => p.id);
    state.battingCycleDismissals = 0;
    state.battingOrderReversed = false;
  } else {
    state.battingOrder = [];
    state.battingCycleDismissals = 0;
    state.battingOrderReversed = false;
  }
}

export function currentInning(state: MatchState): Inning {
  return state.innings[state.currentInnings];
}

export function getBallsPerOver(state: MatchState): number {
  return state.ballsPerOver || 6;
}

export function getBowlerStats(inn: Inning, bowlerId: string, ballsPerOver = 6) {
  let balls = 0;
  let runs = 0;
  let wickets = 0;
  let maidens = 0;
  let ballsInCurrentOver = 0;
  let runsInCurrentOver = 0;

  for (const d of inn.deliveries) {
    if (d.bowlerId !== bowlerId) continue;

    // Byes and leg-byes are team extras — not charged to the bowler
    const isByeOrLegbye = d.extraType === "bye" || d.extraType === "legbye";
    if (!isByeOrLegbye) {
      runs += d.runs;
    }

    // All runs in the over (wides, no-balls, byes, leg-byes) count for maiden
    runsInCurrentOver += d.runs;

    if (d.isLegal) {
      balls += 1;
      ballsInCurrentOver += 1;
      if (ballsInCurrentOver === ballsPerOver) {
        if (runsInCurrentOver === 0) maidens += 1;
        ballsInCurrentOver = 0;
        runsInCurrentOver = 0;
      }
    }
    if (d.isWicket && d.isLegal && d.dismissalType !== "Run Out") wickets += 1;
  }

  const overs = Math.floor(balls / ballsPerOver);
  const remainingBalls = balls % ballsPerOver;
  const oversText = remainingBalls > 0 ? `${overs}.${remainingBalls}` : `${overs}`;
  const economy = balls > 0 ? (runs / (balls / ballsPerOver)).toFixed(2) : "—";

  return { overs: oversText, maidens, runs, wickets, economy, balls };
}

export function addDelivery(state: MatchState, delivery: Delivery): MatchState {
  if (!state.strikerId || !state.nonStrikerId || !state.bowlerId) {
    return state;
  }

  // Create a deep copy to avoid mutating the original state
  const newState = JSON.parse(JSON.stringify(state));
  newState.hasActivity = true;

  const inn = currentInning(newState);

  inn.deliveries.push(delivery);
  inn.totalRuns += delivery.runs;

  if (delivery.extraType === "wide") inn.extras.wides += 1;
  if (delivery.extraType === "noball") inn.extras.noBalls += 1;
  if (delivery.extraType === "bye") inn.extras.byes += 1;
  if (delivery.extraType === "legbye") inn.extras.legByes += 1;

  if (delivery.isLegal) {
    inn.totalBalls += 1;
    inn.legalBalls = inn.totalBalls;
  }

  if (delivery.isWicket) {
    inn.wickets += 1;

    if (newState.format === "INDOOR") {
      inn.totalRuns -= 3;
    }

    const battingTeam = inn.battingTeam === "A" ? newState.teamAPlayers : newState.teamBPlayers;
    const bowlingTeam = inn.battingTeam === "A" ? newState.teamBPlayers : newState.teamAPlayers;
    const dismissedId = delivery.batterId || newState.strikerId;
    const batter = battingTeam.find(p => p.id === dismissedId);
    const bowler = bowlingTeam.find(p => p.id === newState.bowlerId);

    if (batter) {
      batter.dismissal = delivery.dismissal || "out";

      if (newState.format === "INDOOR") {
        batter.outsCount = (batter.outsCount || 0) + 1;
        batter.isOut = true;
      } else {
        batter.isOut = true;
      }

      if (delivery.dismissalType) {
        const fielder = delivery.fielderId ? bowlingTeam.find(p => p.id === delivery.fielderId) : null;
        batter.dismissalDetails = {
          type: delivery.dismissalType,
          bowlerName: bowler?.name,
          fielderName: fielder?.name,
        };
      }
    }

    // Clear the dismissed batter's crease slot (striker or non-striker)
    if (dismissedId === newState.nonStrikerId) {
      newState.nonStrikerId = null;
    } else {
      newState.strikerId = null;
    }

    if (newState.format === "INDOOR") {
      newState.battingCycleDismissals += 1;

      // Track dismissal order for reverse batting
      if (!inn.dismissalHistory) inn.dismissalHistory = [];
      if (batter) inn.dismissalHistory.push(batter.id);

      console.log('[Indoor] Wicket recorded. battingCycleDismissals:', newState.battingCycleDismissals,
        '/ team size:', battingTeam.length,
        '| dismissalHistory:', inn.dismissalHistory,
        '| dismissedPlayers:', battingTeam.filter(p => p.isOut).map(p => p.name));

      const REVERSE_BATTING_THRESHOLD = 6;

      if (newState.battingCycleDismissals >= REVERSE_BATTING_THRESHOLD && !inn.reverseBattingActive) {
        // 6th wicket just fell - activate reverse batting phase
        inn.reverseBattingActive = true;
        console.log('[Indoor] 6th wicket reached - REVERSE BATTING PHASE activated. Dismissed order (oldest first):', inn.dismissalHistory);
      }

      // Full cycle complete (all players dismissed once) - reset for next cycle
      if (newState.battingCycleDismissals >= battingTeam.length) {
        newState.battingCycleDismissals = 0;
        inn.dismissalHistory = [];
        inn.reverseBattingActive = false;
        battingTeam.forEach(p => {
          p.isOut = false;
        });
        console.log('[Indoor] Full batting cycle complete - all players reset');
      }
    }
  }

  // Credit batter runs/balls. Prefer delivery.batterId — the player who was
  // striker when the delivery was recorded — so runs never transfer to a different
  // player after a dismissal, new batter, re-entry, undo, refresh or state update.
  // Fall back to newState.strikerId only when batterId was not stored (legacy).
  const creditBatterId = delivery.batterId || newState.strikerId;
  if (creditBatterId) {
    const battingTeam = inn.battingTeam === "A" ? newState.teamAPlayers : newState.teamBPlayers;
    const batter = battingTeam.find(p => p.id === creditBatterId);
    if (batter) {
      let batterRuns = delivery.runs;

      if (delivery.extraType === "noball" || delivery.extraType === "wide") {
        batterRuns = Math.max(0, delivery.runs - 1);
      } else if (delivery.extraType === "bye" || delivery.extraType === "legbye") {
        batterRuns = 0;
      }

      if (delivery.isLegal || delivery.extraType === "noball") {
        batter.runs += batterRuns;
        if (delivery.isLegal) {
          batter.balls += 1;
        }
        if (batterRuns === 4) batter.fours += 1;
        if (batterRuns === 6) batter.sixes += 1;
      }
    }
  }

  // Striker rotation: swap for odd runs (1, 3, 5)
  // Wall Bonus (W+1) credits 2 runs BUT rotates as if 1 run (ODD = swap strike)
  if ((delivery.isLegal || delivery.extraType === "noball") && !delivery.isWicket) {
    let runsForRotation = delivery.runsForRotation ?? delivery.runs;

    // Special case: W+1 credits 2 runs but rotates as if 1 run taken
    if (delivery.wallBonus) {
      runsForRotation = 1;
    } else if (delivery.extraType === "noball" || delivery.extraType === "wide") {
      runsForRotation = Math.max(0, delivery.runs - 1);
    }

    const shouldSwap = (runsForRotation === 1 || runsForRotation === 3 || runsForRotation === 5);
    if (shouldSwap) {
      const temp = newState.strikerId;
      newState.strikerId = newState.nonStrikerId;
      newState.nonStrikerId = temp;
    }
  }

  if (delivery.isLegal && inn.totalBalls % (newState.ballsPerOver || 6) === 0) {
    const temp = newState.strikerId;
    newState.strikerId = newState.nonStrikerId;
    newState.nonStrikerId = temp;
    newState.bowlerId = null;
  }

  // Auto-end innings only when overs limit is reached (not on wickets for Indoor)
  const currentLimit = newState.innings[newState.currentInnings].oversLimit;
  if (currentLimit !== null && inn.totalBalls >= currentLimit * (newState.ballsPerOver || 6)) {
    inn.completed = true;

    // Auto-start next innings if this isn't the last innings
    if (newState.currentInnings + 1 < newState.inningsCount && !newState.innings[newState.currentInnings + 1]) {
      // Need to start next innings
      const newBattingTeam = inn.bowlingTeam;
      const newBowlingTeam = inn.battingTeam;
      const targetValue = inn.totalRuns + 1;

      newState.innings.push({
        battingTeam: newBattingTeam,
        bowlingTeam: newBowlingTeam,
        deliveries: [],
        totalRuns: 0,
        wickets: 0,
        totalBalls: 0,
        legalBalls: 0,
        completed: false,
        oversLimit: newState.oversLimit,
        target: targetValue,
        extras: { wides: 0, noBalls: 0, byes: 0, legByes: 0 },
        dismissalHistory: [],
        reverseBattingActive: false,
      });
    }
  }

  // Check if 2nd innings target is reached (for T20/ODI)
  if (newState.format !== "INDOOR" && inn.target && inn.totalRuns >= inn.target) {
    inn.completed = true;
  }

  // In non-Indoor modes, auto-end innings if all wickets are down
  if (newState.format !== "INDOOR") {
    const battingTeam = inn.battingTeam === "A" ? newState.teamAPlayers : newState.teamBPlayers;
    const playersOut = battingTeam.filter(p => p.isOut).length;
    if (playersOut >= battingTeam.length - 1) {
      inn.completed = true;
    }
  }

  newState.updatedAt = new Date().toISOString();
  return newState;
}

export function setNextBatter(state: MatchState, playerId: string, preferredSlot?: "striker" | "nonstriker"): MatchState {
  const newState = JSON.parse(JSON.stringify(state));
  if (preferredSlot === "nonstriker" && !newState.nonStrikerId) {
    newState.nonStrikerId = playerId;
  } else if (preferredSlot === "striker" && !newState.strikerId) {
    newState.strikerId = playerId;
  } else if (!newState.strikerId) {
    newState.strikerId = playerId;
  } else if (!newState.nonStrikerId) {
    newState.nonStrikerId = playerId;
  }
  const inn = currentInning(newState);
  const battingTeam = inn.battingTeam === "A" ? newState.teamAPlayers : newState.teamBPlayers;
  const incoming = battingTeam.find((p: Player) => p.id === playerId);
  if (incoming) incoming.isRetiredHurt = false;
  newState.updatedAt = new Date().toISOString();
  return newState;
}

export function setNextBowler(state: MatchState, playerId: string): MatchState {
  const newState = JSON.parse(JSON.stringify(state));
  newState.bowlerId = playerId;
  newState.updatedAt = new Date().toISOString();
  return newState;
}

export function endInnings(state: MatchState): void {
  const inn = currentInning(state);
  inn.completed = true;
  state.updatedAt = new Date().toISOString();
}

export function startNextInnings(state: MatchState): void {
  const inn = currentInning(state);

  if (state.currentInnings + 1 < state.inningsCount) {
    state.currentInnings += 1;

    const prevInning = state.innings[state.currentInnings - 1];
    const newBattingTeam = prevInning.bowlingTeam;
    const newBowlingTeam = prevInning.battingTeam;

    // Indoor mode sets target as first innings score (not +1)
    const targetValue = prevInning.totalRuns + 1;

    state.innings.push({
      battingTeam: newBattingTeam,
      bowlingTeam: newBowlingTeam,
      deliveries: [],
      totalRuns: 0,
      wickets: 0,
      totalBalls: 0,
      legalBalls: 0,
      completed: false,
      target: targetValue,
      oversLimit: prevInning.oversLimit,
      extras: { wides: 0, noBalls: 0, byes: 0, legByes: 0 },
      dismissalHistory: [],
      reverseBattingActive: false,
    });

    state.strikerId = null;
    state.nonStrikerId = null;
    state.bowlerId = null;

    // Reset player stats for new innings
    const battingTeam = newBattingTeam === "A" ? state.teamAPlayers : state.teamBPlayers;
    battingTeam.forEach(p => {
      p.runs = 0;
      p.balls = 0;
      p.fours = 0;
      p.sixes = 0;
      p.isOut = false;
      p.dismissal = null;
      p.dismissalDetails = undefined;
      if (state.format === "INDOOR") {
        p.outsCount = 0;
      }
    });

    if (state.format === "INDOOR") {
      state.battingOrder = battingTeam.map(p => p.id);
      state.battingCycleDismissals = 0;
      state.battingOrderReversed = false;
    }
  }

  state.updatedAt = new Date().toISOString();
}

export function getMatchResult(state: MatchState): string | null {
  if (state.currentInnings === 0) return null;
  if (state.currentInnings === 1 && state.innings.length < 2) return null;

  const firstInnings = state.innings[0];
  const secondInnings = state.innings[1];

  if (!secondInnings || !secondInnings.completed) return null;

  const firstTeamName = firstInnings.battingTeam === "A" ? state.teamAName : state.teamBName;
  const secondTeamName = secondInnings.battingTeam === "A" ? state.teamAName : state.teamBName;

  if (secondInnings.totalRuns > firstInnings.totalRuns) {
    const wicketsRemaining = state.teamAPlayers.length - secondInnings.wickets;
    const ballsRemaining = (secondInnings.oversLimit || 0) * 6 - secondInnings.totalBalls;
    return `${secondTeamName} won by ${wicketsRemaining} wickets (${ballsRemaining} balls remaining)`;
  } else if (firstInnings.totalRuns > secondInnings.totalRuns) {
    const runDifference = firstInnings.totalRuns - secondInnings.totalRuns;
    return `${firstTeamName} won by ${runDifference} runs`;
  } else {
    return "Match tied";
  }
}

export function applyToss(state: MatchState, winner: "A" | "B", decision: "BAT" | "BOWL"): void {
  state.toss = { winnerTeam: winner, decision };

  const battingTeam = decision === "BAT" ? winner : (winner === "A" ? "B" : "A");
  const bowlingTeam = battingTeam === "A" ? "B" : "A";

  if (state.innings[0]) {
    state.innings[0].battingTeam = battingTeam;
    state.innings[0].bowlingTeam = bowlingTeam;
  }

  if (state.format === "INDOOR") {
    const battingPlayers = battingTeam === "A" ? state.teamAPlayers : state.teamBPlayers;
    state.battingOrder = battingPlayers.map(p => p.id);
    state.battingCycleDismissals = 0;
    state.battingOrderReversed = false;
  }

  state.updatedAt = new Date().toISOString();
}

export function getCurrentRunRate(inn: Inning, ballsPerOver = 6): number {
  if (inn.totalBalls === 0) return 0;
  const overs = inn.totalBalls / ballsPerOver;
  return inn.totalRuns / overs;
}

// Required Run Rate for a chase.
// Only meaningful when a target has been set (i.e. the chasing innings).
// runsRequired = target - score; ballsRemaining = scheduledOvers*ballsPerOver - legalBalls.
// Uses legal balls only (wides/no-balls never advance totalBalls; byes/leg-byes do).
// Returns 0 once the target is reached, and null when the chase can no longer
// continue (no legal balls remaining) or when RRR would be undefined — never
// NaN, Infinity or a negative value.
export function getRequiredRunRate(inn: Inning, ballsPerOver = 6): number | null {
  if (!inn.target || !inn.oversLimit) return null;
  const runsRequired = inn.target - inn.totalRuns;
  if (runsRequired <= 0) return 0;
  const ballsRemaining = inn.oversLimit * ballsPerOver - inn.totalBalls;
  if (ballsRemaining <= 0) return null;
  const rrr = (runsRequired * ballsPerOver) / ballsRemaining;
  if (!Number.isFinite(rrr) || rrr < 0) return null;
  return rrr;
}

export function getOversDisplay(totalBalls: number, ballsPerOver = 6): string {
  const overs = Math.floor(totalBalls / ballsPerOver);
  const balls = totalBalls % ballsPerOver;
  return `${overs}.${balls}`;
}

export function getPartnership(state: MatchState, ballsPerOver = 6): { runs: number; balls: number } {
  const inn = currentInning(state);
  const striker = state.strikerId;
  const nonStriker = state.nonStrikerId;

  if (!striker || !nonStriker) {
    return { runs: 0, balls: 0 };
  }

  let partnershipRuns = 0;
  let partnershipBalls = 0;
  let currentStriker = striker;
  let currentNonStriker = nonStriker;

  for (let i = inn.deliveries.length - 1; i >= 0; i--) {
    const del = inn.deliveries[i];

    if (del.isWicket) break;

    if (del.isLegal) {
      partnershipRuns += del.runs;
      partnershipBalls += 1;

      if (del.runs === 1 || del.runs === 3 || del.runs === 5) {
        const temp = currentStriker;
        currentStriker = currentNonStriker;
        currentNonStriker = temp;
      }

      if (partnershipBalls % ballsPerOver === 0) {
        const temp = currentStriker;
        currentStriker = currentNonStriker;
        currentNonStriker = temp;
      }
    }

    if ((currentStriker !== striker || currentNonStriker !== nonStriker) &&
        (currentStriker !== nonStriker || currentNonStriker !== striker)) {
      break;
    }
  }

  return { runs: partnershipRuns, balls: partnershipBalls };
}

export function getProjectedScore(inn: Inning, ballsPerOver = 6): number | null {
  if (!inn.oversLimit || inn.totalBalls === 0) return null;
  const crr = getCurrentRunRate(inn, ballsPerOver);
  return Math.round(crr * inn.oversLimit);
}

export function undoLastDelivery(state: MatchState): MatchState {
  const originalInn = currentInning(state);

  if (originalInn.deliveries.length === 0) {
    return state;
  }

  const lastDelivery = originalInn.deliveries[originalInn.deliveries.length - 1];
  const ballsPerOver = state.ballsPerOver || 6;

  // Deep copy so React and autosave see a new object reference
  const newState = JSON.parse(JSON.stringify(state)) as MatchState;
  const inn = currentInning(newState);

  inn.totalRuns -= lastDelivery.runs;

  if (lastDelivery.extraType === "wide") inn.extras.wides -= 1;
  if (lastDelivery.extraType === "noball") inn.extras.noBalls -= 1;
  if (lastDelivery.extraType === "bye") inn.extras.byes -= 1;
  if (lastDelivery.extraType === "legbye") inn.extras.legByes -= 1;

  if (lastDelivery.isLegal) {
    inn.totalBalls -= 1;
    inn.legalBalls = inn.totalBalls;
  }

  if (lastDelivery.isWicket) {
    inn.wickets -= 1;

    if (newState.format === "INDOOR") {
      inn.totalRuns += 3;
    }

    const battingTeam = inn.battingTeam === "A" ? newState.teamAPlayers : newState.teamBPlayers;
    const batter = battingTeam.find(p => p.id === lastDelivery.batterId);
    if (batter) {
      batter.isOut = false;
      batter.dismissal = null;
      batter.dismissalDetails = undefined;

      if (newState.format === "INDOOR" && batter.outsCount && batter.outsCount > 0) {
        batter.outsCount -= 1;
      }
    }

    if (newState.format === "INDOOR") {
      if (newState.battingCycleDismissals > 0) {
        newState.battingCycleDismissals -= 1;
      }
      if (inn.dismissalHistory && inn.dismissalHistory.length > 0) {
        inn.dismissalHistory.pop();
      }
      const REVERSE_BATTING_THRESHOLD = 6;
      if (inn.reverseBattingActive && newState.battingCycleDismissals < REVERSE_BATTING_THRESHOLD) {
        inn.reverseBattingActive = false;
      }
    }

    // Restore dismissed batter to the correct crease slot
    if (lastDelivery.batterId && !newState.strikerId) {
      newState.strikerId = lastDelivery.batterId;
    } else if (lastDelivery.batterId && !newState.nonStrikerId) {
      newState.nonStrikerId = lastDelivery.batterId;
    }

    // Reverse crossing swap done in handleDismissal for Run Out / Caught
    if (lastDelivery.battersCrossed) {
      const temp = newState.strikerId;
      newState.strikerId = newState.nonStrikerId;
      newState.nonStrikerId = temp;
    }
  }

  // Reverse batter runs/balls for ALL deliveries (including wicket deliveries with runs, e.g. NB+Run Out)
  if (lastDelivery.batterId) {
    const battingTeam = inn.battingTeam === "A" ? newState.teamAPlayers : newState.teamBPlayers;
    const batter = battingTeam.find(p => p.id === lastDelivery.batterId);
    if (batter) {
      let batterRuns = lastDelivery.runs;

      if (lastDelivery.extraType === "noball" || lastDelivery.extraType === "wide") {
        batterRuns = Math.max(0, lastDelivery.runs - 1);
      } else if (lastDelivery.extraType === "bye" || lastDelivery.extraType === "legbye") {
        batterRuns = 0;
      }

      if (lastDelivery.isLegal || lastDelivery.extraType === "noball") {
        batter.runs -= batterRuns;
        if (lastDelivery.isLegal) {
          batter.balls -= 1;
        }
        if (batterRuns === 4) batter.fours -= 1;
        if (batterRuns === 6) batter.sixes -= 1;
      }
    }
  }

  // Reverse striker rotation for non-wicket deliveries
  if ((lastDelivery.isLegal || lastDelivery.extraType === "noball") && !lastDelivery.isWicket) {
    let runsForRotation = lastDelivery.runsForRotation ?? lastDelivery.runs;

    if (lastDelivery.wallBonus) {
      runsForRotation = 1;
    } else if (lastDelivery.extraType === "noball" || lastDelivery.extraType === "wide") {
      runsForRotation = Math.max(0, lastDelivery.runs - 1);
    }

    const rotationSwap = (runsForRotation === 1 || runsForRotation === 3 || runsForRotation === 5);
    const overEndSwap = lastDelivery.isLegal && (inn.totalBalls % ballsPerOver === ballsPerOver - 1);

    if (rotationSwap !== overEndSwap) {
      const temp = newState.strikerId;
      newState.strikerId = newState.nonStrikerId;
      newState.nonStrikerId = temp;
    }

    if (overEndSwap && lastDelivery.bowlerId) {
      newState.bowlerId = lastDelivery.bowlerId;
    }
  }

  inn.deliveries.pop();

  newState.updatedAt = new Date().toISOString();
  return newState;
}

export type BatterStatus = "yetToBat" | "batting" | "retired" | "out";

function battingPlayersForCurrentInning(state: MatchState): Player[] {
  const inn = currentInning(state);
  return inn.battingTeam === "A" ? state.teamAPlayers : state.teamBPlayers;
}

// Every batter is always in exactly one state: yetToBat, batting, retired or out.
export function getBatterStatus(state: MatchState, playerId: string): BatterStatus {
  const player = battingPlayersForCurrentInning(state).find((p) => p.id === playerId);
  if (!player) return "yetToBat";
  if (player.isOut) return "out";
  if (player.isRetiredHurt) return "retired";
  if (state.strikerId === playerId || state.nonStrikerId === playerId) return "batting";
  return "yetToBat";
}

// Retire Hurt: the batter leaves the crease but is NOT out and keeps their score.
// They can be selected again later in the innings. Only a batter currently at the
// crease can be retired.
export function retireHurtBatter(state: MatchState, playerId: string): MatchState {
  if (state.strikerId !== playerId && state.nonStrikerId !== playerId) {
    return state;
  }
  const newState = JSON.parse(JSON.stringify(state));
  const player = battingPlayersForCurrentInning(newState).find((p: Player) => p.id === playerId);
  if (!player) return state;

  player.isRetiredHurt = true;
  player.isOut = false;
  if (newState.strikerId === playerId) newState.strikerId = null;
  if (newState.nonStrikerId === playerId) newState.nonStrikerId = null;

  newState.updatedAt = new Date().toISOString();
  return newState;
}

// Put a retired-hurt batter back at the crease in the first open slot.
export function returnRetiredBatter(state: MatchState, playerId: string): MatchState {
  const newState = JSON.parse(JSON.stringify(state));
  const player = battingPlayersForCurrentInning(newState).find((p: Player) => p.id === playerId);
  if (!player) return state;

  if (!newState.strikerId) {
    newState.strikerId = playerId;
  } else if (!newState.nonStrikerId) {
    newState.nonStrikerId = playerId;
  } else {
    return state;
  }
  player.isRetiredHurt = false;
  player.isOut = false;
  newState.updatedAt = new Date().toISOString();
  return newState;
}

// Admin Correction: return a batter to a valid, available state ("Yet to Bat").
// Reverses an accidental dismissal (restoring the wicket tally, extras and bowling
// figures by neutralising the wicket delivery) or an accidental retirement, and
// clears the batter off the crease so the scorer can reselect. Batting stats already
// accrued from legal deliveries are preserved.
export function adminRestoreBatter(state: MatchState, playerId: string): MatchState {
  const newState = JSON.parse(JSON.stringify(state));
  const inn = currentInning(newState);
  const battingTeam = inn.battingTeam === "A" ? newState.teamAPlayers : newState.teamBPlayers;
  const player = battingTeam.find((p: Player) => p.id === playerId);
  if (!player) return state;

  if (player.isOut) {
    for (let i = inn.deliveries.length - 1; i >= 0; i--) {
      const del = inn.deliveries[i];
      if (del.isWicket && del.batterId === playerId) {
        del.isWicket = false;
        del.dismissal = null;
        del.dismissalType = undefined;
        del.fielderId = null;
        if (del.display === "W") del.display = del.runs > 0 ? String(del.runs) : "•";
        inn.wickets = Math.max(0, inn.wickets - 1);

        if (newState.format === "INDOOR") {
          inn.totalRuns += 3;
          if (player.outsCount && player.outsCount > 0) player.outsCount -= 1;
          if (newState.battingCycleDismissals > 0) newState.battingCycleDismissals -= 1;
          if (inn.dismissalHistory && inn.dismissalHistory.length > 0) {
            const idx = inn.dismissalHistory.lastIndexOf(playerId);
            if (idx >= 0) inn.dismissalHistory.splice(idx, 1);
          }
          const REVERSE_BATTING_THRESHOLD = 6;
          if (inn.reverseBattingActive && newState.battingCycleDismissals < REVERSE_BATTING_THRESHOLD) {
            inn.reverseBattingActive = false;
          }
        }
        break;
      }
    }
  }

  player.isOut = false;
  player.isRetiredHurt = false;
  player.dismissal = null;
  player.dismissalDetails = undefined;

  if (newState.strikerId === playerId) newState.strikerId = null;
  if (newState.nonStrikerId === playerId) newState.nonStrikerId = null;

  if (inn.completed && newState.format !== "INDOOR") {
    const playersOut = battingTeam.filter((p: Player) => p.isOut).length;
    const allOut = playersOut >= battingTeam.length - 1;
    const targetReached = !!inn.target && inn.totalRuns >= inn.target;
    const oversDone = inn.oversLimit != null && inn.totalBalls >= inn.oversLimit * (state.ballsPerOver || 6);
    if (!allOut && !targetReached && !oversDone) {
      inn.completed = false;
    }
  }

  newState.updatedAt = new Date().toISOString();
  return newState;
}
